<template>
    <div class="main" :class="{'theme-dark':displayConfig.theme === 'dark'}">
        <template v-if="errorMessage">
            <div class="error-message">
                <button @click="closeModal" class="close-modal" tabindex="0"><span class="cross"></span></button>
                {{ errorMessage }}
            </div>
        </template>
        <template v-else>

            <link :href="displayConfig.font_primary" rel="stylesheet">

            <div v-show="loading" class="loading"></div>

            <div class="layout" :class="['layout--' + displayConfig.layout, {'loading':loading}]">
                <button @click="closeModal" class="close-modal" :aria-label="trans('Fermer')" tabindex="1"><span
                    class="cross"></span></button>

                <header class="header" :class="headerVariants">

                    <img v-if="displayConfig.display_cover" :src="product.image" alt="" class="cover">
                    <h1 v-if="displayConfig.display_title" :class="title1Small">{{ product.title_1 }}</h1>

                    <div class="details">
                        <h2 v-if="displayConfig.display_authors" class="authors" :class="authorsSmall">{{
                                product.authors
                            }}</h2>

                        <div v-if="displayConfig.display_eans" class="eans">
                            <ul v-if="product.type === 'physical'">
                                <li><strong>{{ $t('EAN physique') }}</strong> {{ product.ean }}</li>
                                <li v-if="product.related_digitals && product.related_digitals.length > 0">
                                    <strong>{{ $t('EAN numérique') }} {{ product.related_digitals[0].ean }}</strong>
                                </li>
                            </ul>
                            <ul v-else-if="product.type === 'digital'">
                                <li><strong>{{ $t('EAN numérique') }}</strong> {{ product.ean }}</li>
                                <li v-if="product.related_physicals && product.related_physicals.length > 0">
                                    <strong>{{ $t('EAN physique') }}</strong> {{ product.related_physicals[0].ean }}
                                </li>
                            </ul>
                            <ul v-else-if="product.type === 'pure_digital'">
                                <li><strong>{{ $t('EAN numérique') }}</strong> {{ product.ean }}</li>
                            </ul>
                        </div>

                        <div v-if="displayConfig.display_publish_date" class="parution">
                            <strong>{{ $t('Parution') }}</strong> {{ formatDate(product.publish_date) }}
                        </div>
                    </div>

                </header>

                <main>
                    <div class="retailers-tabs" role="tablist">
                        <template v-if="product.type==='physical'">
                            <tab-physical
                                v-if="displayConfig.display_physical"
                                :class="{active:currentTab==='physical'}"
                                :aria-selected="currentTab==='physical'"
                                aria-controls="tab-physical-panel"
                                @click="currentTab='physical'"
                                :country="product.country"
                                :price="product.prices.FR"
                                :format_id="product.format_id"
                                :display_price="displayConfig.display_price"
                            ></tab-physical>
                            <tab-digital
                                v-if="displayConfig.display_digital && product.related_digitals.length > 0"
                                :class="{active:currentTab==='digital'}"
                                :aria-selected="currentTab==='digital'"
                                aria-controls="tab-digital-panel"
                                @click="currentTab='digital'"
                                :country="product.country"
                                :price="product.related_digitals[0].prices[product.country]"
                                :format_id="product.related_digitals[0].format_id"
                                :discount-price="product.related_digitals[0].discounts[product.country]?.price"
                                :discount-date-start="product.related_digitals[0].discounts[product.country]?.date_start"
                                :discount-date-end="product.related_digitals[0].discounts[product.country]?.date_end"
                                :display_price="displayConfig.display_price"
                            ></tab-digital>
                        </template>
                        <template v-else-if="product.type==='digital' || product.type==='pure_digital'">
                            <tab-physical
                                v-if="displayConfig.display_physical && product.related_physicals?.length > 0"
                                :class="{active:currentTab==='physical'}"
                                :aria-selected="currentTab==='physical'"
                                aria-controls="tab-physical-panel"
                                @click="currentTab='physical'"
                                :country="product.country"
                                :price="product.related_physicals[0].prices.FR"
                                :format_id="product.related_physicals[0].format_id"
                                :display_price="displayConfig.display_price"
                            ></tab-physical>
                            <tab-digital
                                v-if="displayConfig.display_digital"
                                :class="{active:currentTab==='digital'}"
                                :aria-selected="currentTab==='digital'"
                                aria-controls="tab-digital-panel"
                                @click="currentTab='digital'"
                                :country="product.country"
                                :price="product.prices[product.country]"
                                :format_id="product.format_id"
                                :discount-price="product.discounts[product.country]?.price"
                                :discount-date-start="product.discounts[product.country]?.date_start"
                                :discount-date-end="product.discounts[product.country]?.date_end"
                                :display_price="displayConfig.display_price"
                            ></tab-digital>
                        </template>
                    </div>

                    <div class="retailers-tabs-content">
                        <div
                            v-if="displayConfig.display_physical"
                            id="tab-physical-panel"
                            :class="{'map-first': displayConfig.map_first}"
                            v-show="currentTab==='physical'"
                            role="tabpanel"
                            :aria-hidden="currentTab!=='physical'"
                            aria-labelledby="tab-physical"
                        >
                            <RetailersGrid
                                :retailers-prop="product.type === 'physical' ? 'retailers': 'related_physicals[0].retailers'"
                            ></RetailersGrid>

                            <BookstoresMap v-if="displayConfig.display_map || typeof displayConfig.display_map === 'undefined'"></BookstoresMap>
                        </div>
                        <div
                            v-if="displayConfig.display_digital"
                            id="tab-digital-panel"
                            v-show="currentTab!=='physical'"
                            role="tabpanel"
                            :aria-hidden="currentTab!=='digital'"
                            aria-labelledby="tab-digital"
                        >
                            <RetailersGrid
                                :retailers-prop="product.type === 'physical' ? 'merged_digital_retailers' : 'retailers'"></RetailersGrid>
                        </div>
                    </div>
                </main>

                <template v-if="['CA', 'CH'].indexOf(product.country) < 0">
                    <footer class="footer">
                        <p v-if="(product.country === 'FR' || (product.country === 'BE' && currentTab === 'physical')) && displayConfig.display_price">
                            {{ $t('Mention prix France') }}</p>
                        <p v-else-if="product.country === 'BE' && displayConfig.display_price">{{ $t('Mention prix Belgique') }}</p>

                        <p v-if="currentTab !== 'physical' && product.discounts && useHasDiscount(product.discounts[product.country]?.price, product.discounts[product.country]?.date_start, product.discounts[product.country]?.date_end) && displayConfig.display_price">
                            {{
                                $t('Mention promo', {
                                    date: Intl.DateTimeFormat('fr-FR', {
                                        year: 'numeric',
                                        month: 'numeric',
                                        day: 'numeric'
                                    }).format(Date.parse(product.discounts[product.country].date_end))
                                })
                            }}</p>
                    </footer>
                </template>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import {computed, provide, ref} from "vue";
import TabDigital from "./components/TabDigital.vue";
import TabPhysical from "./components/TabPhysical.vue";
import {useHasDiscount} from "./composables.js";
import RetailersGrid from "./components/RetailersGrid.vue";
import {trans} from "laravel-vue-i18n";
import BookstoresMap from "./components/BookstoresMap.vue";

// Display config
const displayConfig = ref({});
const headerVariants = computed(() => {
    const classes = [];
    if (displayConfig.value.display_eans || displayConfig.value.display_publish_date) {
        classes.push('header--layout-2');
    } else {
        classes.push('header--layout-1');
    }
    if (!displayConfig.value.display_title) {
        classes.push('header--no-title');
    }
    if (!displayConfig.value.display_cover) {
        classes.push('header--no-cover');
    }
    if (!displayConfig.value.display_authors) {
        classes.push('header--no-authors');
    }
    return classes;
});
const title1Small = computed(() => {
    return product.value.title_1?.length > 90 ? 'small' : false;
});
const authorsSmall = computed(() => {
    return title1Small.value || product.value.authors?.length > 100 ? 'small' : false;
});

// Product data
const product = ref({});
const useFallbackCountry = ref(false);

function formatDate(date) {
    return new Date(Date.parse(date)).toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });
}

const retailersPhysical = computed(() => {
    if (product.value.type === 'physical') {
        return product.value.retailers;
    } else if (product.value.type === 'digital') {
        return product.value.related_physicals[0]?.retailers;
    }
    return [];
});
const retailersDigital = computed(() => {
    if (product.value.type === 'digital' || product.value.type === 'pure_digital') {
        return product.value.retailers;
    } else if (product.value.type === 'physical') {
        return product.value.merged_digital_retailers;
    }
    return [];
});

// UI
const loading = ref(true);
const currentTab = ref(tab); // tab is a global variable defined in modal.blade.php
const errorMessage = ref('');
const currentDomain = ref(domain); // domain is a global variable defined in modal.blade.php

const windowConsentGeolocation = computed(() => {
    return consentGeolocation;
})

// fetch data (ean, domain, consentGeolocation are global variables defined in modal.blade.php)
function load() {
    axios
        .get('/api/display?ean=' + ean + '&domain=' + currentDomain.value + '&consentGeolocation=' + windowConsentGeolocation.value)
        .then(response => {
            if (response.status === 200) {
                if (typeof response.data.displayConfig.font_family_name === 'undefined') {
                    response.data.displayConfig.font_family_name = 'inherit';
                }
                displayConfig.value = response.data.displayConfig;
                product.value = response.data.product;
                useFallbackCountry.value = response.data.useFallbackCountry;
                if (tab === '') {
                    currentTab.value = product.value.type === 'pure_digital' ? 'digital' : product.value.type;
                }
                if (product.value.type === 'pure_digital') {
                    currentTab.value = 'digital';
                }
                if ((product.value.type === 'digital' || product.value.type === 'pure_digital') && currentTab.value === 'physical' && !retailersPhysical.value) {
                    currentTab.value = 'digital';
                } else if (product.value.type === 'physical' && currentTab.value === 'digital' && !retailersDigital.value) {
                    currentTab.value = 'physical';
                }
            } else {
                document.body.classList.add('error', 'start');
                errorMessage.value = trans('Une erreur est survenue.');
            }
        })
        .catch(error => {
            document.body.classList.add('error', 'start');
            if (error.response.status === 404) {
                errorMessage.value = error.response.data.message;
            } else {
                errorMessage.value = trans('Une erreur est survenue.');
            }
        })
        .finally(() => {
            loading.value = false;
            if (myOrigin) {
                // Send ready message to host page
                window.parent.postMessage('ready', myOrigin);
            }
        });
}

// Call to API to get display and retailers parameters
load();

// provides
provide('displayConfig', displayConfig);
provide('product', product);
provide('currentDomain', currentDomain);

// close modal
function closeModal() {
    // myOrigin is a global variable defined in modal.blade.php
    document.body.classList.remove('start');
    if (myOrigin) {
        window.parent.postMessage('closeModal', myOrigin)
    }
}

document.body.addEventListener('click', ev => {
    if (ev.target.classList.contains('main')) {
        closeModal()
    }
});

document.addEventListener('keyup', ev => {
    if (ev.key === 'Escape') {
        closeModal();
    }
});

// handle the start and autoStart
window.addEventListener('message', evt => {
    console.log('[Modal.vue] window received message with data ' + evt.data, evt);
    if (evt.data === 'start') {
        document.body.classList.add('start');
    } else if (typeof evt.data === 'string') {
        const matchesOrigin = evt.data.match('^origin:(.*)');
        if (matchesOrigin) {
            (<any>window).myOrigin = matchesOrigin[1];
            // Send ready message to host page
            window.parent.postMessage('ready', myOrigin);
        }
    }
});
if (autoStart) {
    document.body.classList.add('start');
}
</script>

<style>
.main {
    --color-bg-header: v-bind('displayConfig.color_bg_header');
    --color-text-header: v-bind('displayConfig.color_text_header');
    --color-primary: v-bind('displayConfig.color_primary');
    font-family: v-bind('displayConfig.font_family_name');
}
</style>
